import { styled } from '@mui/material';
import { forwardRef } from 'react';
import Avatar from 'shell/components/Avatar';
import Dropdown from 'shell/components/Dropdown';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { getUserPreferredFullName } from 'utils/helper';

const UserChip = ({ disabled, user, onClick }) => {
    return (
        <CustomStack alignItems={'center'} direction={'row'} spacing={1}>
            <Avatar size={'small'} user={user} />
            <CustomText>{getUserPreferredFullName(user)}</CustomText>
            <RemoveIconButton
                aria-label={'remove collaborator'}
                disabled={disabled}
                kind={'exit'}
                size={'small'}
                variant={'square'}
                onClick={() => onClick(user.owner_account_id)}
            />
        </CustomStack>
    );
};

const CollaboratorInput = forwardRef(({ id, disabled, options, value, onChange, ...rest }, ref) => {
    if (!id) throw new Error('Shell: Missing id in component CollaboratorInput.');

    const getUserById = (ownerAccountId) => {
        return options.find(({ owner_account_id }) => owner_account_id === ownerAccountId);
    };

    const remainingOptions = options.filter((option) => !value.some((foo) => foo === option.owner_account_id));

    return (
        <RootStack ref={ref} {...rest} alignItems={'center'} direction={'row'} spacing={1}>
            {value.map((id) => (
                <UserChip
                    key={id}
                    disabled={disabled}
                    user={getUserById(id)}
                    onClick={(id) => onChange(value.filter((item) => item !== id))}
                />
            ))}
            {remainingOptions.length ? (
                <Dropdown
                    id={id}
                    align={'left'}
                    options={remainingOptions.map((option) => ({
                        key: option.owner_account_id,
                        value: (
                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                <Avatar size={'small'} user={option} />
                                {getUserPreferredFullName(option)}
                            </Stack>
                        ),
                    }))}
                    rootComponent={
                        <AddIconButton
                            aria-label={'add collaborator'}
                            disabled={disabled}
                            kind={'add'}
                            size={'small'}
                            variant={'square'}
                        />
                    }
                    onSelect={({ key }) => onChange([...value, key])}
                />
            ) : null}
        </RootStack>
    );
});

const RootStack = styled(Stack)(({ theme }) => {
    return {
        flexWrap: 'wrap',
        padding: theme.spacing(2.5),
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '20px',
        padding: theme.spacing(0.5, 1, 0.5, 0.5),
        background: theme.palette.ensightful.neutral[100],
    };
});

const CustomText = styled(Text)(() => {
    return {
        marginBottom: '-2px',
    };
});

const RemoveIconButton = styled(IconButton)(({ theme }) => {
    return {
        borderRadius: '50%',
        width: '1.25rem',
        height: '1.25rem',
        background: theme.palette.ensightful.neutral[400],
        color: theme.palette.ensightful.shades[0],
        '& > svg': {
            fontSize: '0.5rem',
        },
        '&:hover': {
            background: theme.palette.ensightful.neutral[500],
            color: theme.palette.ensightful.shades[0],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[600],
        },
    };
});

const AddIconButton = styled(IconButton)(() => {
    return {
        borderRadius: '50%',
    };
});

export default CollaboratorInput;
